import { createContext, useContext, useMemo, useState, useEffect } from "react";

const DICT = {
  en: {
    "nav.product": "Product", "nav.templates": "Templates", "nav.pricing": "Pricing",
    "nav.blog": "Blog", "nav.enterprise": "Enterprise", "nav.login": "Sign in",
    "nav.start": "Start creating", "nav.studio": "Open Studio",
    "hero.title1": "CREATE YOUR MOVIE", "hero.title2": "WITH AI.",
    "hero.sub": "From a single image to an entire cinematic production. Direct acts, sequences and shots — Studio Movies IA assembles the film.",
    "hero.cta1": "Create your first scene", "hero.cta2": "Explore the studio",
    "hero.badge": "1 free 5-second video — no card required",
    "demo.title": "One image becomes a film",
    "demo.sub": "Nothing is asked of a model in one giant request. A production is built shot by shot.",
    "demo.s1": "One image", "demo.s2": "Five shots", "demo.s3": "One sequence", "demo.s4": "One film",
    "feat.title": "A studio, not a prompt box",
    "pricing.title": "Plans that scale with your production",
    "pricing.sub": "Every plan runs on Movies Credits. Cost is always shown before you generate.",
    "pricing.monthly": "Monthly", "pricing.yearly": "Yearly", "pricing.packs": "Credit packs",
    "pricing.choose": "Choose", "pricing.current": "Current plan", "pricing.popular": "Most popular",
    "pricing.buy": "Buy credits", "pricing.contact": "Contact sales",
    "tpl.title": "150 director-grade templates",
    "tpl.sub": "Cinematic, animation, advertising, social, music, fashion and storytelling presets.",
    "faq.title": "Frequently asked questions",
    "cta.final": "Your first scene is 60 seconds away.",
    "footer.rights": "All rights reserved.",
    "auth.signin": "Sign in", "auth.signup": "Create your account",
    "auth.email": "Email", "auth.password": "Password", "auth.name": "Name",
    "auth.google": "Continue with Google", "auth.or": "or",
    "auth.noAccount": "No account yet?", "auth.hasAccount": "Already have an account?",
    "auth.forgot": "Forgot password?",
    "studio.home": "Home", "studio.create": "Create", "studio.quick": "Quick Create", "studio.variations": "Variations", "studio.projects": "Projects",
    "studio.storyboards": "Storyboards", "studio.characters": "Characters",
    "studio.locations": "Locations", "studio.assets": "Assets", "studio.templates": "Templates",
    "studio.audio": "Audio", "studio.exports": "Exports", "studio.credits": "Credits",
    "studio.settings": "Settings", "studio.greeting": "Good evening",
    "studio.ask": "What are you creating today?", "studio.generate": "Generate",
    "studio.estimated": "Estimated cost", "studio.available": "Available",
  },
  fr: {
    "nav.product": "Produit", "nav.templates": "Templates", "nav.pricing": "Tarifs",
    "nav.blog": "Blog", "nav.enterprise": "Entreprise", "nav.login": "Connexion",
    "nav.start": "Commencer", "nav.studio": "Ouvrir le studio",
    "hero.title1": "CRÉEZ VOTRE FILM", "hero.title2": "AVEC L'IA.",
    "hero.sub": "D'une simple image à une production cinématographique complète. Dirigez actes, séquences et plans — Studio Movies IA assemble le film.",
    "hero.cta1": "Créer votre première scène", "hero.cta2": "Explorer le studio",
    "hero.badge": "1 vidéo de 5 secondes offerte — sans carte bancaire",
    "demo.title": "Une image devient un film",
    "demo.sub": "Rien n'est demandé au modèle en une seule fois. Une production se construit plan par plan.",
    "demo.s1": "Une image", "demo.s2": "Cinq plans", "demo.s3": "Une séquence", "demo.s4": "Un film",
    "feat.title": "Un studio, pas un champ de prompt",
    "pricing.title": "Des offres qui suivent votre production",
    "pricing.sub": "Toutes les offres fonctionnent avec les Movies Credits. Le coût est affiché avant chaque génération.",
    "pricing.monthly": "Mensuel", "pricing.yearly": "Annuel", "pricing.packs": "Packs de crédits",
    "pricing.choose": "Choisir", "pricing.current": "Offre actuelle", "pricing.popular": "Le plus choisi",
    "pricing.buy": "Acheter des crédits", "pricing.contact": "Nous contacter",
    "tpl.title": "150 templates de niveau réalisateur",
    "tpl.sub": "Presets cinéma, animation, publicité, social, musique, mode et narration.",
    "faq.title": "Questions fréquentes",
    "cta.final": "Votre première scène est à 60 secondes.",
    "footer.rights": "Tous droits réservés.",
    "auth.signin": "Connexion", "auth.signup": "Créer votre compte",
    "auth.email": "E-mail", "auth.password": "Mot de passe", "auth.name": "Nom",
    "auth.google": "Continuer avec Google", "auth.or": "ou",
    "auth.noAccount": "Pas encore de compte ?", "auth.hasAccount": "Vous avez déjà un compte ?",
    "auth.forgot": "Mot de passe oublié ?",
    "studio.home": "Accueil", "studio.create": "Créer", "studio.quick": "Création rapide", "studio.variations": "Variantes", "studio.projects": "Projets",
    "studio.storyboards": "Storyboards", "studio.characters": "Personnages",
    "studio.locations": "Décors", "studio.assets": "Médias", "studio.templates": "Templates",
    "studio.audio": "Audio", "studio.exports": "Exports", "studio.credits": "Crédits",
    "studio.settings": "Réglages", "studio.greeting": "Bonsoir",
    "studio.ask": "Que créez-vous aujourd'hui ?", "studio.generate": "Générer",
    "studio.estimated": "Coût estimé", "studio.available": "Disponible",
  },
};

const Ctx = createContext({ locale: "en", t: (k) => k, setLocale: () => {} });

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    const m = window.location.pathname.match(/^\/(en|fr)(\/|$)/);
    if (m) return m[1];
    return localStorage.getItem("moviz_locale") || (navigator.language?.startsWith("fr") ? "fr" : "en");
  });

  useEffect(() => {
    localStorage.setItem("moviz_locale", locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, t: (k) => DICT[locale]?.[k] ?? DICT.en[k] ?? k }),
    [locale]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);
